import {
  ChangeEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react'

import * as Dialog from '@radix-ui/react-dialog'
import Cropper, { Area } from 'react-easy-crop'
import { GraphQL } from '@guestgoodie/graphql'
import { Trans, useLingui } from '@lingui/react/macro'

import { dialogContainer, DialogHeader } from '@/layouts/Dialog'
import Button from '@/components/ui/Button'
import { getCroppedImg } from '@/utils/cropHelpers'
import { showToast, ToastType } from '@/features/toast'
import Loading from './Loading'

type CommonProps = {
  backgroundColor?: string
  onDone: (file: File, metadata?: GraphQL.ProcessingMetadata) => void
  onCancel: () => void
}

type OuterOptionalProps = {
  aspectRatio: number
  allowPadding: boolean
  shape: 'round' | 'rect'
}

type CropFileProps = CommonProps &
  OuterOptionalProps & {
    file: File
    type: 'IMAGE' | 'VIDEO'
  }

function EditFile({
  aspectRatio,
  allowPadding,
  shape,
  file,
  type,
  backgroundColor,
  onDone,
  onCancel
}: CropFileProps) {
  const { t } = useLingui()

  const [src, setSrc] = useState<string>()
  const [isLoading, setIsLoading] = useState(false)
  const [crop, setCrop] = useState({ x: 0, y: 0 })
  const [zoom, setZoom] = useState(1)
  const [didMediaLoad, setDidMediaLoad] = useState(false)

  useEffect(() => {
    if (!file) return

    const src = URL.createObjectURL(file)

    setSrc(src)

    return () => URL.revokeObjectURL(src)
  }, [file])

  const croppedPixels = useRef<Area | null>(null)

  const onCropComplete = (_croppedArea: Area, croppedAreaPixels: Area) => {
    croppedPixels.current = croppedAreaPixels
  }

  const onSaveImage = useCallback(() => {
    if (!croppedPixels.current || !src) return

    setIsLoading(true)

    getCroppedImg(src, croppedPixels.current, file.name)
      .then(file => onDone(file))
      .catch(error => {
        console.error('Error cropping the image', error)

        showToast({
          id: 'crop-error',
          type: ToastType.ERROR,
          message: t({
            comment: 'Toast Error message when an unexpected error happened.',
            message: `An error occurred while cropping your image, please try again, or if it still doesn't work write our support.`
          })
        })
      })
      .finally(() => setIsLoading(false))
  }, [src, file.name, onDone, setIsLoading, t])

  const onSaveVideo = useCallback(() => {
    if (!croppedPixels.current) return

    onDone(file, {
      cropping: croppedPixels.current
    })
  }, [file, onDone])

  const onSave = useCallback(() => {
    if (type === 'IMAGE') onSaveImage()
    if (type === 'VIDEO') onSaveVideo()
  }, [type, onSaveImage, onSaveVideo])

  const onChangeZoom = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      const newZoom = parseFloat(event.target.value)

      setZoom(newZoom)
    },
    [setZoom]
  )

  return (
    <div className="flex flex-col gap-5">
      <DialogHeader
        title={t`Crop file`}
        description={t`Cropping of a file to perfectly show it in the GuestApp.`}
        close={onCancel}
      />
      <div className="flex flex-col pb-3 h-[20vh] relative rounded overflow-hidden">
        <Cropper
          image={type === 'IMAGE' ? src : undefined}
          video={type === 'VIDEO' ? src : undefined}
          crop={crop}
          zoom={zoom}
          aspect={aspectRatio}
          minZoom={allowPadding ? 0.3 : 1}
          cropShape={shape}
          restrictPosition={allowPadding ? false : true}
          onCropChange={setCrop}
          onCropComplete={onCropComplete}
          onZoomChange={setZoom}
          onMediaLoaded={() => setDidMediaLoad(true)}
          style={{
            containerStyle: {
              backgroundColor
            }
          }}
        />
        {!didMediaLoad && (
          <div className="flex flex-col justify-center items-center absolute inset-0 bg-glass-strong gap-2">
            <Loading />
            <span className="text-regular tex-text max-w-1/3 text-center">
              <Trans>
                File is loading, this can happen if the file isn&apos;t
                immediately available to the system
              </Trans>
            </span>
          </div>
        )}
      </div>
      <div>
        <input
          type="range"
          min={allowPadding ? '0.3' : '1'}
          step={0.1}
          max={10}
          value={zoom}
          onChange={onChangeZoom}
          className="w-full accent-primary bg-primary-light"
        />
      </div>
      <footer className="flex flex-row items-center justify-end gap-4">
        <Button variant="outline" onClick={onCancel} type="button">
          <Trans>Cancel</Trans>
        </Button>
        <Button onClick={onSave} loading={isLoading} disabled={!src}>
          <Trans>Choose</Trans>
        </Button>
      </footer>
    </div>
  )
}

type Props = CommonProps &
  Partial<OuterOptionalProps> & {
    file?: File | null
  }

export default function CropperDialog({
  aspectRatio = 1,
  allowPadding = false,
  shape = 'rect',
  backgroundColor = 'transparent',
  file,
  onDone,
  onCancel
}: Props) {
  const onOpenChange = useCallback(
    (open: boolean) => {
      // We assume cancel if radix requests an open change to false
      if (!open) onCancel()
    },
    [onCancel]
  )

  const type = useMemo(() => {
    if (file?.type.startsWith('image/')) return 'IMAGE'
    if (file?.type.startsWith('video/')) return 'VIDEO'
    return null
  }, [file])

  return (
    <Dialog.Root open={!!file && !!type} onOpenChange={onOpenChange}>
      <Dialog.Portal container={dialogContainer}>
        <Dialog.Overlay className="fixed size-full inset-0 bg-black/30 grid place-items-center overflow-y-auto z-top-dialog">
          <Dialog.Content className="relative w-screen max-w-[700px] py-10 px-2">
            <div className="shadow-md rounded bg-white flex flex-col gap-4 p-5">
              {file && type && (
                <EditFile
                  allowPadding={allowPadding}
                  aspectRatio={aspectRatio}
                  file={file}
                  type={type}
                  onCancel={onCancel}
                  onDone={onDone}
                  shape={shape}
                  backgroundColor={backgroundColor}
                />
              )}
            </div>
          </Dialog.Content>
        </Dialog.Overlay>
      </Dialog.Portal>
    </Dialog.Root>
  )
}
